import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import { api } from "@/shared/api/client";
import { errorMessage } from "@/shared/i18n";
import { invalidate } from "@/shared/lib/query";

/** The company's logo, which heads its documents: shown, replaced (PNG or JPEG) or removed. */
export function LogoPicker({ companyId, fileId, disabled, onChange }: { companyId: string; fileId?: string | null; disabled?: boolean; onChange?: () => unknown }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await invalidate(qc, ["/v1/core/companies"]);
      await onChange?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const upload = (file: File) => run(() => {
    const fd = new FormData();
    fd.append("file", file);
    return api.put(`/v1/core/companies/${companyId}/logo`, fd);
  });
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-32 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-ctl)] border border-line bg-white p-1">
        {fileId
          ? <img src={`/api/v1/core/files/${fileId}/content?inline=true`} alt={t("settings.logo")} className="max-h-full max-w-full object-contain" />
          : <ImagePlus className="size-6 text-ink-3" aria-hidden />}
      </div>
      <div className="flex min-w-0 flex-col items-start gap-1">
        {!disabled && <span className="flex flex-wrap gap-1">
          <button type="button" className="btn-secondary py-1" disabled={busy} onClick={() => input.current?.click()}>{fileId ? t("settings.logoChange") : t("settings.logoAdd")}</button>
          {fileId && <button type="button" className="btn-ghost py-1 text-danger" disabled={busy} onClick={() => run(() => api.del(`/v1/core/companies/${companyId}/logo`))}>{t("settings.logoRemove")}</button>}
        </span>}
        <span className="text-[12px] text-ink-3">{t("settings.logoHint")}</span>
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" aria-label={t("settings.logo")}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} />
    </div>
  );
}
